import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_URLS } from '../constants';
import { authenticatedFetch } from '../utils/api';

const SettingsPage = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: user?.email || '',
    function: user?.role || '',
    planType: 'Basic'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const location = useLocation();

  // Update form data when user data changes
  React.useEffect(() => {
    if (user) {
      const nameParts = (user.name || '').split(' ');
      setFormData({
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '',
        email: user.email || '',
        function: user.role || '',
        planType: 'Basic'
      });
    }
  }, [user]);

  // Handle email update success from verification page
  React.useEffect(() => {
    if (location.state?.emailUpdateSuccess) {
      setMessage({ 
        type: 'success', 
        text: `Email successfully updated to ${location.state.newEmail}!` 
      });
      // Clear the state to prevent showing message on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);



  const handleCancel = () => {
    const nameParts = (user?.name || '').split(' ');
    setFormData({
      firstName: nameParts[0] || '',
      lastName: nameParts.slice(1).join(' ') || '',
      email: user?.email || '',
      function: user?.role || '',
      planType: 'Basic'
    });
    setMessage(null);
  };

  const handleSave = async () => {
    setIsLoading(true);
    setMessage(null);

    try {
      const response = await authenticatedFetch(API_URLS.USER_PROFILE.UPDATE, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: `${formData.firstName} ${formData.lastName}`.trim(),
          role: formData.function
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Update user context with new data
        setUser(data.user);
        setMessage({ type: 'success', text: 'Profile updated successfully!' });
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to update profile' });
      }
    } catch (error) {
      console.error('Profile update error:', error instanceof Error ? error.message : 'Unknown error');
      setMessage({ type: 'error', text: 'An error occurred while updating your profile' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateEmail = () => {
    navigate('/update-email');
  };

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const FUNCTION_OPTIONS = [
    { value: '', label: 'Select Function' },
    { value: 'Developer', label: 'Developer' },
    { value: 'Designer', label: 'Designer' },
    { value: 'Product Manager', label: 'Product Manager' },
    { value: 'Data Scientist', label: 'Data Scientist' },
    { value: 'Marketing', label: 'Marketing' },
    { value: 'Other', label: 'Other' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-4 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Account Settings Card */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-4 py-4">
            <div className="mb-4">
              <h1 className="text-xl font-semibold text-gray-900">Account Settings</h1>
            </div>

            {/* Success/Error Message */}
            {message && (
              <div className={`mb-3 p-2 rounded-md ${
                message.type === 'success' 
                  ? 'bg-green-50 border border-green-200 text-green-800' 
                  : 'bg-red-50 border border-red-200 text-red-800'
              }`}>
                {message.text}
              </div>
            )}

            <div className="space-y-3">
                {/* Full Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                    Full Name:
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      className="px-2 py-1.5 border border-gray-300 rounded-md text-sm text-left focus:outline-none focus:ring-1 focus:ring-brand-electric-blue focus:border-brand-electric-blue"
                      placeholder="First Name"
                    />
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      className="px-2 py-1.5 border border-gray-300 rounded-md text-sm text-left focus:outline-none focus:ring-1 focus:ring-brand-electric-blue focus:border-brand-electric-blue"
                      placeholder="Last Name"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                    Email:
                  </label>
                  <div className="px-2 py-1.5 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-700 text-left">
                    {user?.email || 'Not available'}
                  </div>
                </div>

                {/* Function */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                    Function:
                  </label>
                  <select
                    value={formData.function}
                    onChange={(e) => handleInputChange('function', e.target.value)}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm text-left focus:outline-none focus:ring-1 focus:ring-brand-electric-blue focus:border-brand-electric-blue"
                  >
                    {FUNCTION_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Email Verified */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                    Email Verified:
                  </label>
                  <div className="px-2 py-1.5 bg-green-50 border border-green-200 rounded-md text-sm text-green-600 font-medium text-left">
                    Yes
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 mt-4">
                <button
                  onClick={handleSave}
                  disabled={isLoading}
                  className="px-4 py-2 bg-brand-dark-blue text-white text-sm font-medium rounded-md hover:bg-brand-electric-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-electric-blue disabled:opacity-50"
                >
                  {isLoading ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={isLoading}
                  className="px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
          </div>
        </div>

        {/* Update Email Address Card */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-4 py-4">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Update Email Address</h2>
            {user?.isSSOOnly || user?.hasSSO ? (
              <div className="space-y-3">
                <div className="bg-brand-light-gray border border-gray-200 rounded-md p-3">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-brand-electric-blue" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-brand-dark-blue">
                        SSO Account Email Restriction
                      </h3>
                      <div className="mt-2 text-sm text-brand-dark-blue">
                        <p>
                          Your account is linked to {user.ssoProviders?.join(', ') || 'SSO'} authentication. 
                          Email updates are managed through your SSO provider. If you need to change your email 
                          address, please contact support.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  disabled
                  className="px-4 py-2 bg-gray-300 text-gray-500 text-sm font-medium rounded-md cursor-not-allowed"
                >
                  Update Email Address (Disabled for SSO)
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-gray-600">
                  Need to change your email address? Click the button below to start the verification process.
                </p>
                <button
                  onClick={handleUpdateEmail}
                  className="px-4 py-2 bg-brand-dark-blue text-white text-sm font-medium rounded-md hover:bg-brand-electric-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-electric-blue"
                >
                  Update Email Address
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;